import type { IdNameResult } from '@/api/useMetadata/types'
import { cmp, orderBy } from '@/utils/orderBy'
import { Form, type FormItemProps, type FormRule, Select } from 'antd'
import dayjs from 'dayjs'
import { groupBy } from 'lodash'
import { useMemo } from 'react'
import { useScheduleItems } from '../../Context/ScheduleItems/ScheduleItemsContext'
import type { MappedScheduleItem, ScheduleOverlapArgs } from '../../Context/ScheduleItems/scheduleItemStore'
import { conflictValidators } from '../../Context/ScheduleItems/validators'
import type { ConflictError, ConflictValidationType } from '../../utils/ConflictValidators'

const formatOverlapText = (errors: ConflictError<MappedScheduleItem>[], prefix = ' - ') => {
  if (!errors.length) return ''
  //we'll just take the first error
  const err = errors[0]
  const errText = errors.some((x) => x.type === 'InactiveTractor')
    ? 'Inactive'
    : errors.some((x) => x.type === 'OutOfServiceTractor')
      ? 'Out of Service'
      : err.items.length > 1
        ? 'multiple drivers'
        : (err.items[0]?.driver_name ?? 'Unknown Driver')
  return prefix + errText
}

const createConflictError = (
  items: { value: string; conflicts: ConflictError<MappedScheduleItem>[] }[],
  errorType: ConflictValidationType,
  otherOpts: Partial<Omit<FormRule, 'validator'>> = {}
): FormRule => {
  return {
    validator: (_, value) => {
      if (!value || !value.value || typeof value.value !== 'string') return Promise.resolve()
      const conflicts = items.find((x) => x.value === value.value)?.conflicts
      if (!conflicts?.length) return Promise.resolve()
      const err = conflicts.find((x) => x.type === errorType)
      if (!err) return Promise.resolve()
      return Promise.reject(new Error(err.value))
    },
    ...otherOpts,
  }
}

const createConflictRules = (
  items: { value: string; conflicts: ConflictError<MappedScheduleItem>[] }[]
): FormRule[] => {
  return Object.values(conflictValidators.validatorsByType).map((v) =>
    createConflictError(items, v.type, { warningOnly: v.type !== 'InactiveTractor' })
  )
}

export type ConflictSelectProps = FormItemProps & {
  event: Partial<ScheduleOverlapArgs> & { depot_name?: string | null }
}
type ConflictSelectType = {
  items: Array<IdNameResult & { depot?: string | null }>
  loading?: boolean
  itemKey: keyof Pick<MappedScheduleItem, 'trailer_id' | 'tractor_id'>
}

export function ConflictDependentSelect({
  items,
  loading,
  event: { start, end, event_id, depot_name },
  itemKey: key,
  ...props
}: ConflictSelectProps & ConflictSelectType) {
  const { getOverlappingSchedules } = useScheduleItems()
  const overlappingSchedules = useMemo(() => {
    const overlaps = start && end ? getOverlappingSchedules({ start, end, event_id }) : []
    return groupBy(overlaps, (x) => x[key])
  }, [getOverlappingSchedules, start, end, key, event_id])
  const mappedItems = useMemo(() => {
    const day = dayjs()
    const result = items.map((x) => {
      const { errors } = conflictValidators.getInvalidItems(
        { start: day, end: day, driver_or_carrier_id: '', event_id: '', editable: false, [key]: x.id },
        overlappingSchedules[x.id] ?? []
      )
      return {
        value: x.id,
        data: x,
        conflicts: errors,
        label: `${x.name} (${x.depot ?? 'No Location'})` + formatOverlapText(errors, ' - '),
      }
    })
    return result.sort(
      orderBy<(typeof result)[0]>((x) => x.conflicts.some((y) => y.type === 'InactiveTractor'))
        .thenByDesc((x) => depot_name && x.data.depot === depot_name)
        .thenBy((x) => x.data.name, cmp.string)
    )
  }, [items, overlappingSchedules, key, depot_name])
  return (
    <Form.Item {...props} rules={[...createConflictRules(mappedItems), ...(props.rules ?? [])]}>
      <Select optionFilterProp='label' options={mappedItems} labelInValue showSearch loading={loading} allowClear />
    </Form.Item>
  )
}
